/* eslint-disable jsx-a11y/alt-text */
import { IonIcon, IonItem, IonRow, IonText } from '@ionic/react';
import HStack from 'components/HStack';
import { star, starOutline, videocam } from 'ionicons/icons';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { ST } from 'types';
import { useStores } from '../../../models';
import { RunModel } from '../../../models/RunModel/RunModel';
import { toSpeed } from '../../../util';
import { useUnitConversion } from 'hooks/useUnitConversion';

export const scaleData = (
  value: number | undefined,
  suffix: string = ''
): string => {
  if (value === undefined) return `?? ${suffix}`;
  if (value < 1 && suffix === 'm') {
    suffix = 'cm';
    value *= 100;
  }

  return `${value.toFixed(2)} ${suffix}`;
};

export const formatValue = (
  value: number | undefined,
  suffix: string = ''
): string => {
  if (value === undefined) return `?? ${suffix}`;
  return `${value.toFixed(2)} ${suffix}`;
};

export const calcHighestSpeed = (steps?: ST.PlayStep[]): number | undefined => {
  if (!steps) return undefined;

  return steps.reduce((highest: number, current: ST.PlayStep) => {
    return Math.max(highest, toSpeed(current.v_ms));
  }, 0);
};

interface RunDataProps {
  item: RunModel;
  figureName: string;
  distance: number;
  airtime: number;
  height: number;
  width: number;
  score: number;
}

const RunData: React.FC<RunDataProps> = observer(
  ({ item, distance, airtime, height, width, figureName, score }) => {
    const { currentUser } = useStores();
    const unitConversion = useUnitConversion();

    const starClicked = (ev: React.MouseEvent) => {
      ev.preventDefault();
      if (!item.shareUrl || !currentUser) return;

      currentUser.toggleFavorite(item.shareUrl).then((ret) => {
        if (ret == null) return; // error thrown.
        item!.setFavorite(ret);
      });
      return false;
    };

    const _itemInfo = useMemo(() => {
      // Use unit conversion for all distance measurements
      let distanceObj = unitConversion.scaleDistance(distance);
      let heightObj = unitConversion.scaleDistance(height);
      let widthObj = unitConversion.scaleDistance(width);
      
      let distanceStr = `${distanceObj.value}${distanceObj.suffix}`;
      let heightStr = `${heightObj.value}${heightObj.suffix}`;
      let widthStr = `${widthObj.value}${widthObj.suffix}`;
      let airTimeStr = scaleData(airtime, 's');
      
      // Use unit conversion for speed display
      const speedValue = calcHighestSpeed(item.playData?.steps);
      let speedStr = '?? km/h';
      if (speedValue !== undefined) {
        const displayData = unitConversion.getDisplay(speedValue, 'speed', 'metric'); // km/h is stored in metric
        speedStr = displayData.formatted;
      }
      
      return {
        distance: distanceStr,
        height: heightStr,
        width: widthStr,
        airTime: airTimeStr,
        speed: speedStr,
      };
    }, [distance, height, width, airtime, item.playData?.steps, unitConversion]);

    // let heightDiffIcon = '/assets/icons/height_difference_up.svg';
    // if (itemInfo.heightDiff?.startsWith('-')) {
    //   heightDiffIcon = '/assets/icons/height_difference_down.svg';
    // }

    return (
      <IonItem>
        <div
          style={{
            paddingTop: '8px',
            paddingBottom: '8px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <IonRow className="ion-justify-content-between ion-align-items-center">
            <IonRow style={{ gap: '0px' }}>
              {item.videoUrl && <IonIcon icon={videocam} />}
              {item.playData && (
                <IonIcon
                  onClick={starClicked}
                  size="default"
                  color="secondary"
                  icon={item.favorite ? star : starOutline}
                />
              )}
            </IonRow>
          </IonRow>

          <HStack>
            <div
              style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: '8px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <div className="trick-name">
                  <IonText>
                    Figure{' '}
                    {figureName.startsWith('Run') ? figureName.slice(4) : figureName}
                  </IonText>
                </div>
                <div className="trick-score" style={{ marginLeft: '16px' }}>
                  <IonText>Score: {score}</IonText>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%' }}>
                <div className="trick-stat" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <IonIcon src="/assets/icons/jump_height.svg" />
                  <IonText>{_itemInfo.height}</IonText>
                </div>
                <div className="trick-stat" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <IonIcon src="/assets/icons/airtime.svg" />
                  <IonText>{_itemInfo.airTime}</IonText>
                </div>
                <div className="trick-stat" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <IonIcon src="/assets/icons/distance.svg" />
                  <IonText>{_itemInfo.distance}</IonText>
                </div>
                <div className="trick-stat" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <IonIcon src="/assets/icons/jump_distance.svg" />
                  <IonText>{_itemInfo.width}</IonText>
                </div>
              </div>
            </div>
          </HStack>
        </div>
      </IonItem>
    );
  }
);

export default RunData;
